import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Typography } from '@telus-uds/components-web';
import { get, round } from 'lodash';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import Delete from '@telus-uds/palette-allium/build/web/icons/Delete';
import Edit from '@telus-uds/palette-allium/build/web/icons/Edit';
import {
  ModalFieldLabel,
  ModalFooter,
  Table,
  Td,
  TdImp,
  TdLabel,
  Th,
  Tr,
  Button as ButtonWrapper
} from './css';
import request from '../../../utils/request';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';
import { getLocale } from '../../../misc';
import { Error } from '../../../design/elements';

const RedeemOfferVulcan = ({
  onChange,
  orderData,
  buttonError,
  setIsFormTouched,
  setShowDownloadQuoteFooter
}) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState();
  const [updateData, setUpdateData] = useState();
  const [isButtonVisible, setIsButtonVisible] = useState(true);
  const [error, setError] = useState(null);
  const [redeemOfferData, setRedeemOfferData] = useState([]);

  const locale = getLocale();

  const {
    vulcanWebform: {
      _couponCode,
      _validationCode,
      _discountAmount,
      _duration,
      _redeemOffer,
      _validateCode,
      _add,
      _couponCodeInvalid,
      _couponCodeExpired
    }
  } = locale === 'en-US' ? EN : FR;

  const onCloseModal = () => {
    setError(null);
    setModalVisible(false);
    setIsButtonVisible(true);
  };

  const showModal = (e) => {
    e.preventDefault();
    setUpdateData(null);
    setModalVisible(true);
  };

  const addData = () => {
    if (updateData) {
      // Update existing row
      const updatedSelections = [...redeemOfferData];
      updatedSelections[redeemOfferData.indexOf(updateData)] = modalData;
      setRedeemOfferData(updatedSelections);
    } else {
      // Add new row
      setRedeemOfferData([...redeemOfferData, modalData]);
    }
    setShowDownloadQuoteFooter(false);
    setIsFormTouched(true);
    setIsButtonVisible(true);
    setModalVisible(false);
  };

  const deleteItem = (e, idx) => {
    e.preventDefault();
    setRedeemOfferData([
      ...redeemOfferData.slice(0, idx),
      ...redeemOfferData.slice(idx + 1)
    ]);
    setShowDownloadQuoteFooter(false);
    setIsFormTouched(true);
  };

  const editItem = (e, row) => {
    e.preventDefault();
    setError(null);
    setUpdateData(row);
    setModalVisible(true);
  };

  const handleFieldChange = (values, setFieldValue, fieldName, fieldValue) => {
    if (fieldName !== 'promotionAmount' || fieldName !== 'duration') {
      setFieldValue('promotionAmount', '');
      setFieldValue('duration', '');
    }
    setIsButtonVisible(true);
    setError(null);
    setFieldValue(fieldName, fieldValue);
  };

  const totals = useMemo(() => {
    let totalAmount = 0;
    redeemOfferData.forEach(item => {
      totalAmount += round(item.promotionAmount, 2);
    });
    return {
      totalCost: totalAmount
    };
  }, [redeemOfferData]);

  const redeemOfferAdjustedTotalPrice = useMemo(() => {
    let monthlyPrice = 0;
    let oneTimePrice = 0;
    redeemOfferData.forEach(item => {
      if (item.duration === 'One-Time') {
        oneTimePrice += round(item.promotionAmount, 2);
      } else {
        monthlyPrice += round(item.promotionAmount, 2);
      }
    });
    return {
      monthlyPrice: monthlyPrice,
      oneTimePrice: oneTimePrice
    };
  }, [redeemOfferData]);

  useEffect(() => {
    onChange({
      redeemOfferSelection: redeemOfferData,
      redeemOfferAdjustedTotalPrice,
      type: 'CouponBasedDiscount'
    });
  }, [redeemOfferData, redeemOfferAdjustedTotalPrice]);

  useEffect(() => {
    if (redeemOfferData.length === 0) {
      setRedeemOfferData(get(orderData, 'redeemOfferSelection', []));
    }
  }, [orderData]);

  const handleWheel = e => e.target.blur();

  return (
    <div>
      {!!redeemOfferData.length && (
        <Table>
          <Th><Typography>{_couponCode}</Typography></Th>
          <Th><Typography>{_validationCode}</Typography></Th>
          <Th><Typography>{_discountAmount}</Typography></Th>
          <Th><Typography>{_duration}</Typography></Th>
          <Th colSpan={2}><Typography>Actions</Typography></Th>{' '}
          {/* Add this column for edit/delete buttons */}
          {redeemOfferData.map(
            (
              { couponCode, validationCode, promotionAmount, duration },
              idx
            ) => (
              <Tr key={`${couponCode}-${validationCode}-${idx}`}>
                <Td><Typography>{couponCode}</Typography></Td>
                <Td><Typography>{validationCode}</Typography></Td>
                <Td><Typography>{promotionAmount}</Typography></Td>
                <Td><Typography>{duration}</Typography></Td>
                <Td>
                  <Button
                    icon={Edit}
                    onPress={e => editItem(e, redeemOfferData[idx])}
                    variant={{ text: true }}
                    accessibilityLabel="Edit"
                  />
                </Td>
                <Td>
                  <Button
                    icon={Delete}
                    onPress={e => deleteItem(e, idx)}
                    variant={{ text: true }}
                    accessibilityLabel="Delete"
                  />
                </Td>
              </Tr>
            )
          )}
          <Tr>
            <TdLabel colSpan={2}>Total Promotion Amount</TdLabel>
            <TdImp><Typography>{totals.totalCost}</Typography></TdImp>
            <Td />
            <Td />
          </Tr>
        </Table>
      )}
      <ButtonWrapper>
        <Button variant={{ priority: 'high' }} onClick={showModal}
          id="redeemOfferData"
          name="redeemOfferData">
          {_redeemOffer}
        </Button>
      </ButtonWrapper>
      <span style={{ marginTop: '10px', display: 'block' }}>
        {buttonError && <Error text={buttonError.text} />}
      </span>
      <Modal
        isOpen={isModalVisible}
        onClose={onCloseModal}
        heading={updateData ? 'Edit Redeem Offer' : _redeemOffer}
        variant={{ width: 'xl' }}
      >
        <Formik
          initialValues={
            updateData || {
              couponCode: '',
              validationCode: '',
              promotionAmount: '',
              duration: ''
            }
          }
          onSubmit={(values, { setFieldValue }) => {
            const exportValues = `/coupons/check-coupon`;
            request
              .post(exportValues, { ...values })
              .then(response => {
                const {
                  isValid,
                  isActive,
                  duration,
                  amount,
                  couponCode,
                  validationCode
                } = response.data;
                if (isValid && isActive) {
                  setFieldValue('promotionAmount', amount);
                  setFieldValue('duration', duration);
                  setModalData({
                    couponCode: couponCode,
                    validationCode: validationCode,
                    promotionAmount: amount,
                    duration: duration
                  });
                  setIsButtonVisible(false);
                }
                if (!isValid) {
                  setError(_couponCodeInvalid);
                } else if (isValid && !isActive) {
                  setError(_couponCodeExpired);
                }
              })
              .catch();
          }}
        >
          {({ values, setFieldValue, submitForm }) => (
            <Form>
              {error && (
                <div style={{ color: 'red', fontSize: '20px' }}><Typography>{error}</Typography></div>
              )}
              <div>
                <ModalFieldLabel htmlFor="couponCode">
                  {_couponCode}*
                </ModalFieldLabel>
                <Field
                  type="text"
                  id="couponCode"
                  name="couponCode"
                  className="frg-input-field color-light"
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                  onChange={e =>
                    handleFieldChange(
                      values,
                      setFieldValue,
                      'couponCode',
                      e.target.value
                    )
                  }
                  required
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="validationCode">
                  {_validationCode}
                </ModalFieldLabel>
                <Field
                  type="text"
                  id="validationCode"
                  name="validationCode"
                  className="frg-input-field color-light"
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                  onChange={e =>
                    handleFieldChange(
                      values,
                      setFieldValue,
                      'validationCode',
                      e.target.value
                    )
                  }
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="promotionAmount">
                  {_discountAmount}
                </ModalFieldLabel>
                <Field
                  type="number"
                  id="promotionAmount"
                  name="promotionAmount"
                  className="frg-input-field color-light"
                  onWheel={handleWheel}
                  disabled
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="cost">{_duration}</ModalFieldLabel>
                <Field
                  type="text"
                  id="duration"
                  name="duration"
                  className="frg-input-field color-light"
                  onWheel={handleWheel}
                  disabled
                  style={{ width: '100%' }}
                />
              </div>
              <ModalFooter>
                <ButtonWrapper>
                  <Button
                    onClick={submitForm}
                    disabled={!isButtonVisible}
                    variant={{ priority: 'high' }}>
                    {_validateCode}
                  </Button>
                </ButtonWrapper>
                <ButtonWrapper>
                  <Button variant={{ priority: 'high' }} disabled={isButtonVisible}
                    onClick={addData}>
                    {_add}
                  </Button>
                </ButtonWrapper>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

RedeemOfferVulcan.propTypes = {
  onChange: PropTypes.func.isRequired,
  orderData: PropTypes.object,
  redeemOfferData: PropTypes.array.isRequired,
  setRedeemOfferData: PropTypes.func.isRequired
};

export default RedeemOfferVulcan;
